import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shell, Card, Steps } from "../components/Layout";
import { WeekGrid } from "../components/ScheduleGrid";
import { PasswordInput } from "../components/PasswordInput";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { apiError, http, GROUPS, groupLabel, DAYS, slotMatchesGroup, BLOCK_SUBJECTS,
  thisMonthISO, TIER_OF } from "../lib/api";

export default function Matricula() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [student, setStudent] = useState({ name: "", stage_group: "", grade: "", start_month: thisMonthISO() });
  const [family, setFamily] = useState({ family_name: "", email: "", phone: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState([]);
  const [prices, setPrices] = useState(null);
  const [done, setDone] = useState(null);

  const logged = user && user.role === "family";
  const total = logged ? 3 : 4;

  useEffect(() => {
    if (student.stage_group) http.get("/slots").then(({ data }) => setSlots(data));
  }, [student.stage_group]);

  useEffect(() => { http.get("/settings/pricing").then(({ data }) => setPrices(data.prices)); }, []);

  useEffect(() => {
    http.get("/settings/start-months").then(({ data }) => {
      setStudent((s) => ({ ...s, start_month: data.options[0] }));
    });
  }, []);

  /** Cuota que le corresponde con las franjas elegidas hasta el momento. */
  const quote = useMemo(() => {
    if (!prices || !student.stage_group) return null;
    const chosen = slots.filter((s) => selected.includes(s.id));
    const big = chosen.filter((s) => s.kind !== "gr").length;
    const grHours = chosen.filter((s) => s.kind === "gr").length;
    const tier = TIER_OF(student.stage_group);
    const bigFee = big ? Number(prices[tier]?.[String(big)] || 0) : 0;
    const grSession = Number(prices.gr_session || 0);
    const grWeeks = 4;
    const grFee = grHours * grWeeks * grSession;
    return { big, grHours, tier, bigFee, grSession, grFee, totalFee: bigFee + grFee };
  }, [prices, slots, selected, student.stage_group]);

  const eligible = (slot) => slotMatchesGroup(slot, student.stage_group);

  const toggle = (slot) =>
    setSelected((prev) => {
      if (prev.includes(slot.id)) return prev.filter((i) => i !== slot.id);
      const chosen = slots.filter((s) => prev.includes(s.id));
      const isGr = slot.kind === "gr";
      const count = chosen.filter((s) => (s.kind === "gr") === isGr).length;
      if (isGr ? count >= 2 : count >= 4) return prev;
      return [...prev, slot.id];
    });

  const finish = async () => {
    setError("");
    setBusy(true);
    try {
      if (!logged) {
        if (!accepted) throw { response: { data: { detail: "Debe aceptar la política de privacidad" } } };
        await register({ ...family, accepted_privacy: true });
      }
      const { data: st } = await http.post("/students", {
        name: student.name, stage_group: student.stage_group, grade: student.grade || null,
        start_month: student.start_month,
      });
      const { data } = await http.post("/enrollments", { student_id: st.id, slot_ids: selected });
      setDone(data);
      setStep(total + 1);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const stepLabels = logged
    ? ["Datos del alumno", "Elección del horario", "Confirmación"]
    : ["Datos del alumno", "Datos de la familia", "Elección del horario", "Confirmación"];

  const scheduleStep = logged ? 2 : 3;
  const confirmStep = logged ? 3 : 4;

  if (done)
    return (
      <Shell accent="centro" title="Matrícula confirmada" testid="matricula-done">
        <Card className="max-w-2xl">
          <p className="text-lg">
            <strong>{done.name}</strong> queda matriculado/a en {groupLabel(done.stage_group)}.
          </p>
          <ul className="mt-4 space-y-2 text-lg text-ink-700">
            {done.slots.map((s) => (
              <li key={s.id} data-testid={`done-slot-${s.id}`}>
                {DAYS[s.day]} de {s.start} a {s.end}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-lg">
            Horas en grupo grande: <strong>{done.weekly_hours}</strong> · Horas de GR:{" "}
            <strong>{done.gr_weekly_hours}</strong> · Mensualidad estimada:{" "}
            <strong>{done.monthly_fee} €</strong> (estado: {done.payment_status})
          </p>
          <Button data-testid="go-profile-btn" onClick={() => navigate("/perfil/centro")}
            className="mt-6 h-14 w-full bg-centro text-lg hover:bg-centro-hover">
            Acceder a mi cuenta
          </Button>
        </Card>
      </Shell>
    );

  return (
    <Shell accent="centro" title="Matrícula en el Centro de Estudios"
      subtitle="El proceso consta de unos pocos pasos. Puede retroceder en cualquier momento."
      testid="matricula-page">
      <Card className="max-w-3xl">
        <Steps step={step} total={total} label={stepLabels[step - 1]} />

        {step === 1 && (
          <div className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-lg">Nombre y apellidos del alumno/a</Label>
              <Input id="name" data-testid="student-name-input" className="mt-2 h-14 text-lg"
                value={student.name} onChange={(e) => setStudent({ ...student, name: e.target.value })} />
            </div>
            <div>
              <p className="text-lg font-medium">Etapa educativa</p>
              <p className="mb-3 text-base text-ink-700">Seleccione la opción que corresponda a su curso.</p>
              <div className="grid gap-3">
                {GROUPS.map((g) => (
                  <button key={g.value} type="button" data-testid={`group-${g.value}-btn`}
                    onClick={() => setStudent({ ...student, stage_group: g.value })}
                    className={`rounded-xl border-2 p-4 text-left text-lg transition-colors ${
                      student.stage_group === g.value ? "border-centro bg-centro-light" : "border-border bg-white hover:border-centro"}`}>
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="grade" className="text-lg">Curso</Label>
              <Input id="grade" data-testid="student-grade-input" className="mt-2 h-14 text-lg"
                placeholder="Por ejemplo, 4º de Primaria"
                value={student.grade} onChange={(e) => setStudent({ ...student, grade: e.target.value })} />
            </div>
            <Button data-testid="step1-next-btn" disabled={!student.name || !student.stage_group}
              onClick={() => setStep(2)} className="h-14 w-full bg-centro text-lg hover:bg-centro-hover">
              Siguiente paso
            </Button>
          </div>
        )}

        {step === 2 && !logged && (
          <div className="space-y-6">
            <div>
              <Label htmlFor="fname" className="text-lg">Nombre del padre, madre o tutor legal</Label>
              <Input id="fname" data-testid="family-name-input" className="mt-2 h-14 text-lg"
                value={family.family_name} onChange={(e) => setFamily({ ...family, family_name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="phone" className="text-lg">Teléfono móvil para los recordatorios por WhatsApp</Label>
              <Input id="phone" data-testid="family-phone-input" className="mt-2 h-14 text-lg"
                placeholder="+34 600 000 000" value={family.phone}
                onChange={(e) => setFamily({ ...family, phone: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="femail" className="text-lg">Correo electrónico (será el usuario de acceso)</Label>
              <Input id="femail" data-testid="family-email-input" type="email" className="mt-2 h-14 text-lg"
                value={family.email} onChange={(e) => setFamily({ ...family, email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="fpass" className="text-lg">Contraseña (mínimo 6 caracteres)</Label>
              <PasswordInput id="fpass" testid="family-password-input" className="mt-2 h-14 text-lg"
                autoComplete="new-password" value={family.password}
                onChange={(e) => setFamily({ ...family, password: e.target.value })} />
              <p className="mt-2 text-sm text-ink-700">
                Pulse el ojo para ver la contraseña y anotarla sin errores.
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-cream p-4">
              <Checkbox id="privacy" data-testid="privacy-checkbox" checked={accepted}
                onCheckedChange={(v) => setAccepted(!!v)} className="mt-1 h-6 w-6" />
              <Label htmlFor="privacy" className="text-base leading-relaxed">
                Como padre, madre o tutor legal del menor, presto mi consentimiento para el
                tratamiento de estos datos y acepto la{" "}
                <Link to="/privacidad" className="underline" target="_blank">Política de Privacidad</Link>.
              </Label>
            </div>
            <div className="flex gap-3">
              <Button data-testid="step2-back-btn" variant="outline" className="h-14 flex-1 text-lg"
                onClick={() => setStep(1)}>Volver atrás</Button>
              <Button data-testid="step2-next-btn" className="h-14 flex-1 bg-centro text-lg hover:bg-centro-hover"
                disabled={!family.family_name || !family.email || !family.phone || family.password.length < 6 || !accepted}
                onClick={() => setStep(3)}>Siguiente paso</Button>
            </div>
          </div>
        )}

        {step === scheduleStep && (
          <div className="space-y-6">
            <p className="text-lg text-ink-700">
              Seleccione las franjas que mejor se ajusten a sus necesidades: hasta 4 horas semanales
              en grupo grande y hasta 2 horas semanales en Grupo Reducido (GR). Las franjas
              completas o que no corresponden a la etapa del alumno aparecen atenuadas y no pueden
              seleccionarse. Se muestra el horario completo del centro, de lunes a viernes y de
              15:30 a 21:30.            </p>
            <p className="text-lg font-medium" data-testid="selected-count">
              Ha seleccionado {selected.filter((id) => slots.find((s) => s.id === id)?.kind !== "gr").length} de 4 horas
              en grupo grande y {selected.filter((id) => slots.find((s) => s.id === id)?.kind === "gr").length} de 2 horas de GR
            </p>
            <WeekGrid slots={slots} selectedIds={selected} onSelect={toggle}
              isEligible={(s) => eligible(s)} />
            {quote && (
              <div className="rounded-xl bg-cream p-5 text-lg" data-testid="live-price">
                <p className="font-medium">Precio que le corresponde con esta selección</p>
                <p className="mt-2">Grupo grande ({quote.tier}), {quote.big} h semanales:{" "}
                  <strong data-testid="live-price-big">{quote.bigFee.toFixed(2)} €</strong> al mes</p>
                {quote.grHours > 0 && (
                  <p>Grupo Reducido, {quote.grHours} h semanales ({quote.grSession.toFixed(2)} € por
                    sesión): <strong data-testid="live-price-gr">{quote.grFee.toFixed(2)} €</strong> al mes
                    (estimación sobre 4 sesiones al mes; se recalcula cada mes según las sesiones realizadas)</p>
                )}
                <p className="mt-2 text-xl">Cuota mensual:{" "}
                  <strong data-testid="live-price-total">{quote.totalFee.toFixed(2)} €</strong></p>
                {student.start_month?.endsWith("-09") && (
                  <p className="mt-2 text-base" data-testid="live-price-discount">
                    Septiembre, con el <strong>30 % de descuento</strong> por el inicio del curso el
                    14 de septiembre:{" "}
                    <strong>{(quote.totalFee * 0.7).toFixed(2)} €</strong>. El descuento se aplica
                    solo a la mensualidad de septiembre.
                  </p>
                )}
              </div>
            )}
            <div className="flex gap-3">
              <Button data-testid="schedule-back-btn" variant="outline" className="h-14 flex-1 text-lg"
                onClick={() => setStep(logged ? 1 : 2)}>Volver atrás</Button>
              <Button data-testid="schedule-next-btn" disabled={selected.length === 0}
                className="h-14 flex-1 bg-centro text-lg hover:bg-centro-hover"
                onClick={() => setStep(confirmStep)}>Siguiente paso</Button>
            </div>
          </div>
        )}

        {step === confirmStep && (
          <div className="space-y-6">
            <h2 className="font-head text-2xl font-medium">Revisión y confirmación</h2>
            <div className="rounded-xl bg-cream p-5 text-lg">
              <p><strong>Alumno/a:</strong> {student.name}</p>
              <p><strong>Etapa:</strong> {groupLabel(student.stage_group)}</p>
              <p><strong>Curso:</strong> {student.grade || "no indicado"}</p>
              {!logged && <p><strong>Familia:</strong> {family.family_name} · {family.phone}</p>}
              <p className="mt-3"><strong>Franjas elegidas:</strong></p>
              <ul className="mt-1 space-y-1">
                {slots.filter((s) => selected.includes(s.id)).map((s) => (
                  <li key={s.id}>
                    {DAYS[s.day]} de {s.start} a {s.end} — {s.label}
                    {s.block ? ` (${s.block}: ${(BLOCK_SUBJECTS[s.block] || []).join(", ")})` : ""}
                  </li>
                ))}
              </ul>
              {quote && (
                <p className="mt-4 text-xl" data-testid="confirm-price">
                  Cuota mensual: <strong>{quote.totalFee.toFixed(2)} €</strong>
                  {student.start_month?.endsWith("-09") && (
                    <span className="block text-base text-ink-700">
                      Septiembre con el 30 % de descuento: {(quote.totalFee * 0.7).toFixed(2)} €.
                    </span>
                  )}
                  {quote.grHours > 0 && (
                    <span className="block text-base text-ink-700">
                      Incluye {quote.bigFee.toFixed(2)} € de grupo grande y {quote.grFee.toFixed(2)} €
                      estimados de Grupo Reducido.
                    </span>
                  )}
                </p>
              )}
            </div>
            {error && <p data-testid="matricula-error" className="rounded-lg bg-red-50 p-4 text-base text-red-700">{error}</p>}
            <div className="flex gap-3">
              <Button data-testid="confirm-back-btn" variant="outline" className="h-14 flex-1 text-lg"
                onClick={() => setStep(scheduleStep)}>Volver atrás</Button>
              <Button data-testid="confirm-matricula-btn" disabled={busy}
                className="h-14 flex-1 bg-centro text-lg hover:bg-centro-hover" onClick={finish}>
                {busy ? "Guardando..." : "Confirmar matrícula"}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </Shell>
  );
}
